import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as Actions from '../redux/actions';
import RecipientSelect from '../components/RecipientSelect';
import { reportViewUrl } from '../utils/reportUrls';
import '../styles/report.scss';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const statusFor = (inRange) => {
  if (inRange === true) {
    return { key: 'ok', label: 'In range', Icon: CheckCircleOutlineIcon };
  }
  if (inRange === false) {
    return { key: 'attention', label: 'Attention', Icon: ErrorOutlineIcon };
  }
  return { key: 'empty', label: 'No tests', Icon: RemoveCircleOutlineIcon };
};

const reportKey = (report) => report.hash_id || report.id;

const ReportHistoryRow = ({
  report,
  expanded,
  onToggle,
  onResend,
  resending,
  resent,
  canResend,
}) => {
  const id = reportKey(report);
  const recipients = report.recipients || [];

  const viewReport = (e) => {
    e.stopPropagation();
    const viewUrl = reportViewUrl(report.hash_id) || report.url;
    if (viewUrl) {
      window.open(viewUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (id) window.open(`/reports/edit?${id}`, '_blank', 'noopener,noreferrer');
  };

  const editReport = (e) => {
    e.stopPropagation();
    if (!id) return;
    window.open(`/reports/edit?${id}`, '_blank', 'noopener,noreferrer');
  };

  const submitted = Array.isArray(report.created)
    ? `${report.created[0]} at ${report.created[1]}`
    : '';
  const meta = [
    report.customer?.name,
    submitted,
    report.completion?.tests > 0
      ? `${report.completion.tests} test${report.completion.tests === 1 ? '' : 's'}`
      : null,
    report.completion?.notes > 0
      ? `${report.completion.notes} note${report.completion.notes === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean).join(' · ');

  const status = statusFor(report.inRange);
  const StatusIcon = status.Icon;

  return (
    <li className="report-history-item">
      <div
        role="button"
        tabIndex={0}
        className={[
          'report-history-row',
          `report-history-row--${status.key}`,
          expanded ? 'expanded' : '',
        ].filter(Boolean).join(' ')}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="report-history-main">
          <div className="report-history-title-line">
            <span className="report-history-title">{report.title}</span>
            {report.author && (
              <span className="report-history-author">by {report.author}</span>
            )}
            {report.edited_after_submit && (
              <span className="report-history-edited" title="Edited after submission">Edited</span>
            )}
          </div>
          {meta ? <span className="report-history-meta">{meta}</span> : null}
        </div>

        <span className={`report-history-status report-history-status--${status.key}`}>
          <StatusIcon className="report-history-status-icon" aria-hidden="true" />
          {status.label}
        </span>

        <ExpandMoreIcon
          className={`report-history-chevron${expanded ? ' expanded' : ''}`}
          aria-hidden="true"
        />
      </div>

      {expanded && (
        <div className="report-history-detail">
          <div className="report-history-detail-actions">
            <button
              type="button"
              className="report-history-detail-btn view"
              onClick={viewReport}
            >
              <VisibilityIcon /> View Report
            </button>
            {report.editable !== false && (
              <button
                type="button"
                className="report-history-detail-btn edit"
                onClick={editReport}
              >
                <EditIcon /> Edit Report
              </button>
            )}
            {canResend && (
              <button
                type="button"
                className={`report-history-detail-btn resend${resent ? ' resent' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onResend(report);
                }}
                disabled={resending}
              >
                {resent ? (
                  <><CheckCircleIcon /> Sent!</>
                ) : resending ? (
                  <><SendIcon /> Sending…</>
                ) : (
                  <><SendIcon /> Resend Report</>
                )}
              </button>
            )}
          </div>

          <div className="report-history-detail-section">
            <div className="report-history-detail-label">
              <EmailIcon className="report-history-detail-label-icon" />
              Recipients
            </div>
            {recipients.length > 0 ? (
              <div className="report-history-detail-recipients">
                {recipients.map((email) => (
                  <a
                    key={email}
                    className="report-history-detail-recipient"
                    href={`mailto:${email}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {email}
                  </a>
                ))}
              </div>
            ) : (
              <div className="report-history-detail-empty">No recipients on file</div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

const ReportHistory = (props) => {
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [resendReport, setResendReport] = useState(null);
  const [resendRecipients, setResendRecipients] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [resentIds, setResentIds] = useState({});
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);

  const history = props.history || [];
  const hasMore = props.historyMeta?.has_more ?? false;
  const totalCount = props.historyMeta?.total_count ?? history.length;

  const fetchPage = useCallback((options = {}) => {
    const {
      offset = 0,
      append = false,
      q = activeQuery,
      showSearching = false,
    } = options;

    const requestId = ++requestIdRef.current;
    if (!append) {
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setExpandedId(null);
    }
    if (showSearching) setSearching(true);
    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    return Promise.resolve(
      props.fetchReportHistory(
        props.authToken,
        () => (requestId === requestIdRef.current ? undefined : false),
        {
          offset,
          limit: PAGE_SIZE,
          append,
          q,
        }
      )
    ).finally(() => {
      if (requestId !== requestIdRef.current) return;
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
      if (showSearching) setSearching(false);
    });
  }, [activeQuery, props.authToken, props.fetchReportHistory]);

  useEffect(() => {
    const nextQuery = searchInput.trim();
    const timer = setTimeout(() => {
      setActiveQuery((current) => (current === nextQuery ? current : nextQuery));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchPage({
      offset: 0,
      append: false,
      q: activeQuery,
      showSearching: true,
    });
  }, [activeQuery, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore) return;

    fetchPage({
      offset: history.length,
      append: true,
      q: activeQuery,
    });
  }, [fetchPage, hasMore, history.length, activeQuery]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, history.length, hasMore]);

  const showEmpty = history.length === 0 && !searching;
  const filtering = activeQuery.length > 0;

  const openResend = (report) => {
    if (resendingId) return;
    setResendReport(report);
    setResendRecipients([...(report.recipients || [])]);
  };

  const closeResend = () => {
    if (resendingId) return;
    setResendReport(null);
    setResendRecipients([]);
  };

  const confirmResend = () => {
    if (!resendReport || resendRecipients.length === 0) return;

    const id = reportKey(resendReport);
    setResendingId(id);

    props.resendReport(
      id,
      props.authToken,
      resendRecipients,
      () => {
        setResendingId(null);
        setResendReport(null);
        setResendRecipients([]);
        setResentIds((current) => ({ ...current, [id]: Date.now() }));
        setTimeout(() => {
          setResentIds((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
        }, 3000);
      },
      () => setResendingId(null)
    );
  };

  const resendContacts = (resendReport?.recipients || []).map((email) => ({ email }));

  return (
    <section className="report-history" aria-label="Report history">
      <div className="report-history-toolbar">
        <div className="report-history-heading">
          <span className="report-history-label">Submitted reports</span>
          {(totalCount > 0 || filtering) && (
            <span className="report-history-count">{totalCount}</span>
          )}
        </div>

        <div className="report-history-search">
          <SearchIcon className="report-history-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="browser-default"
            placeholder="Search customer, title, or author…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search report history"
          />
        </div>
      </div>

      {showEmpty ? (
        <div className="report-history-empty">
          <HistoryIcon className="report-history-empty-icon" aria-hidden="true" />
          <p className="report-history-empty-title">
            {filtering ? 'No matching reports' : 'No submitted reports'}
          </p>
          <p className="report-history-empty-copy">
            {filtering
              ? 'Try a different customer, title, or author.'
              : 'Submitted service reports will show up here.'}
          </p>
        </div>
      ) : (
        <>
          {searching && history.length === 0 ? (
            <p className="report-history-loading">Searching…</p>
          ) : (
            <ul className="report-history-list">
              {history.map((report) => {
                const id = reportKey(report);
                return (
                  <ReportHistoryRow
                    key={id}
                    report={report}
                    expanded={expandedId === id}
                    onToggle={() => setExpandedId((current) => (current === id ? null : id))}
                    onResend={openResend}
                    resending={resendingId === id}
                    resent={!!resentIds[id]}
                    canResend={!props.user?.operator}
                  />
                );
              })}
            </ul>
          )}

          {hasMore && (
            <div
              ref={sentinelRef}
              className="report-history-sentinel"
              aria-hidden="true"
            />
          )}

          {(loadingMore || (searching && history.length > 0)) && (
            <p className="report-history-loading">
              {searching ? 'Searching…' : 'Loading more reports…'}
            </p>
          )}

          {!hasMore && history.length > 0 && !searching && (
            <p className="report-history-end">
              {filtering
                ? `${totalCount} matching report${totalCount === 1 ? '' : 's'}`
                : `Showing all ${totalCount} reports`}
            </p>
          )}
        </>
      )}

      {resendReport && (
        <RecipientSelect
          key={`history-resend-${reportKey(resendReport)}-${resendRecipients.join(',')}`}
          contacts={resendContacts}
          selectedRecipients={resendRecipients}
          handleRecipientSelect={(email) => {
            setResendRecipients((current) =>
              current.includes(email)
                ? current.filter((r) => r !== email)
                : [...current, email]
            );
          }}
          addNewContact={(email) => {
            setResendRecipients((current) =>
              current.includes(email) ? current : [...current, email]
            );
          }}
          submit={confirmResend}
          close={closeResend}
          loading={resendingId === reportKey(resendReport)}
          loadingMessage="Sending..."
        />
      )}
    </section>
  );
};

function mapStateToProps(state) {
  return {
    history: state.reportHistory,
    historyMeta: state.reportHistoryMeta,
    authToken: state.authToken,
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportHistory);
